import React, { FC, useState } from 'react';
import { Box, Affix, Text, Stack, Popover, Group } from '@mantine/core';
import { Button } from 'ui/Button';
import { ReactComponent as KebabMenu } from './img/iconKebab.svg';
import styles from './OrderStepsActions.module.scss';
import { useCanManage } from 'hooks/useCanManage';

interface OrderStepsActionsProps {
  actions:
    | Array<{
        text: string;
        action: string;
        icon: string;
      }>
    | null
    | false;
  isUnitBased: 0 | 1;
  info: React.ReactNode | false;
  onMenuAction: (actionId: string) => void;
  onActionClick: (actionId: string) => void;
  listItems?: Array<{ id: string; name: string; icon: string; color?: string }>;
  loading?: boolean;
  disabled?: boolean;
  confirmVolume?: boolean;
  showPrimaryButton?: boolean;
  showButtonChat?: boolean;
}

export const OrderStepsActions: FC<OrderStepsActionsProps> = ({
  actions,
  isUnitBased,
  info,
  onMenuAction,
  onActionClick,
  listItems = [],
  loading = false,
  disabled = false,
  confirmVolume = false,
  showPrimaryButton = false,
  showButtonChat = false,
}) => {
  const [opened, setOpened] = useState(false);
  const canManage = useCanManage();
  const visibleActions = Array.isArray(actions)
    ? actions.filter(
        (a) => !(confirmVolume && a.action === 'confirm_volume') || showPrimaryButton,
      )
    : actions;

  const visibleListItems = listItems.filter(
    (item) =>
      !(item.id === 'edit' && (isUnitBased === 0 || !confirmVolume)) &&
      (item.id !== 'chat' || showButtonChat),
  );

  return (
    <div className={styles.steps_right}>
      <Group justify='flex-end' gap='12px' align='flex-start' wrap='wrap-reverse'>
        <Popover
          width={270}
          position='bottom-end'
          radius={'4px'}
          opened={opened}
          onChange={setOpened}>
          <Popover.Target>
            <KebabMenu
              className={`${styles.menuToggle} ${
                actions ? `${styles.menuToggle_active}` : ''
              }`}
              onClick={() => setOpened((o) => !o)}
            />
          </Popover.Target>
          <Popover.Dropdown
            p='4px 0'
            style={{
              boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
              border: 'none',
            }}>
            {visibleListItems.map((item) => (
              <Group
                key={item.id}
                p='8px 12px'
                h='40px'
                className={styles.item}
                onClick={() => {
                  onMenuAction(item.id);
                  setOpened(false);
                }}>
                <div className={styles.item_icon}>
                  <img src={item.icon} alt={item.name} />
                  <span style={{ color: item.color }}>{item.name}</span>
                </div>
              </Group>
            ))}
          </Popover.Dropdown>
        </Popover>
        {canManage && visibleActions && visibleActions.length > 0 && (
          <Box visibleFrom='sm' className={styles.buttons_container}>
            {visibleActions.map((a, idx) => (
              <Button
                key={`${a.action}-${idx}`}
                icon
                src={a.icon}
                onClick={() => onActionClick(a.action)}
                loading={loading}
                disabled={disabled || loading}
                className={styles.action_btn}
                theme='filed'>
                {a.text}
              </Button>
            ))}
          </Box>
        )}
        {canManage && visibleActions && visibleActions.length > 0 && (
          <Affix
            position={{ bottom: 0, right: 0, left: 0 }}
            hiddenFrom='sm'
            p='12px 16px 34px 16px'
            style={{ backgroundColor: 'white', borderRadius: '8px 8px 0px 0px' }}>
            <Stack gap='12px'>
              <Text span c='#000' fz='14px' lh='18px'>
                {info}
              </Text>
              <div className={styles.buttons_container}>
                {visibleActions.map((a, idx) => (
                  <Button
                    key={`${a.action}-mobile-${idx}`}
                    icon
                    src={a.icon}
                    onClick={() => onActionClick(a.action)}
                    loading={loading}
                    disabled={disabled || loading}
                    className={styles.action_btn}
                    theme='filed'>
                    {a.text}
                  </Button>
                ))}
              </div>
            </Stack>
          </Affix>
        )}
      </Group>
      <Box visibleFrom='sm'>{info}</Box>
    </div>
  );
};
